/**
 * CommentCard Component - Unified design from GoalDetailScreen
 * Displays a single comment with avatar, name, user type badge, and action menu
 */

import React, { useState, useMemo } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Modal,
  Pressable,
} from 'react-native';
import Svg, { Path, Circle } from 'react-native-svg';
import { FONTS } from '../../constants/config';
import { useTheme } from '../../contexts/ThemeContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { getTextAlign } from '../../utils/rtl';
import { CommentCardProps, USER_TYPE_CONFIG } from './types';

import { tokens } from '@design';
// ============================================================
// SVG Icon Components
// ============================================================

const EllipsisVerticalIcon = ({ size = 16, color = tokens.colors.semantic.general.foreground }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Circle cx="12" cy="12" r="1" fill={color} />
    <Circle cx="12" cy="5" r="1" fill={color} />
    <Circle cx="12" cy="19" r="1" fill={color} />
  </Svg>
);

const EditIcon = ({ size = 16, color = tokens.colors.semantic.general.foreground }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M11 4H4C3.46957 4 2.96086 4.21071 2.58579 4.58579C2.21071 4.96086 2 5.46957 2 6V20C2 20.5304 2.21071 21.0391 2.58579 21.4142C2.96086 21.7893 3.46957 22 4 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V13" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M18.5 2.50001C18.8978 2.10219 19.4374 1.87869 20 1.87869C20.5626 1.87869 21.1022 2.10219 21.5 2.50001C21.8978 2.89784 22.1213 3.4374 22.1213 4.00001C22.1213 4.56262 21.8978 5.10219 21.5 5.50001L12 15L8 16L9 12L18.5 2.50001Z" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const TrashIcon = ({ size = 16, color = tokens.colors.raw.red["500"] }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M3 6H5H21" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M8 6V4C8 3.46957 8.21071 2.96086 8.58579 2.58579C8.96086 2.21071 9.46957 2 10 2H14C14.5304 2 15.0391 2.21071 15.4142 2.58579C15.7893 2.96086 16 3.46957 16 4V6M19 6V20C19 20.5304 18.7893 21.0391 18.4142 21.4142C18.0391 21.7893 17.5304 22 17 22H7C6.46957 22 5.96086 21.7893 5.58579 21.4142C5.21071 21.0391 5 20.5304 5 20V6H19Z" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const XIcon = ({ size = 16, color = tokens.colors.semantic.general.foreground }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M18 6L6 18M6 6L18 18" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

// ============================================================
// CommentCard Component
// ============================================================

const CommentCard: React.FC<CommentCardProps> = ({
  comment,
  onEdit,
  onDelete,
}) => {
  const { colors, isDark } = useTheme();
  const { t } = useLanguage();
  const [menuVisible, setMenuVisible] = useState(false);
  const [showOriginalPopup, setShowOriginalPopup] = useState(false);

  // Theme colors
  const themeColors = {
    cardBackground: isDark ? colors.card : tokens.colors.raw.slate["100"],
    textPrimary: colors.textPrimary,
    textMuted: colors.textMuted,
    border: colors.border,
    dropdownBackground: colors.card,
    iconDefault: colors.iconDefault,
    menuSeparator: isDark ? colors.border : tokens.colors.raw.slate["200"],
    editedTagBg: isDark ? '#78350F' : tokens.colors.raw.amber["100"],
    editedTagText: isDark ? '#FDE68A' : '#92400E',
  };

  const handleEdit = () => {
    setMenuVisible(false);
    onEdit?.(comment);
  };

  const handleDelete = () => {
    setMenuVisible(false);
    onDelete?.(comment.id);
  };

  // Get user type label text (matches Figma exactly)
  const getUserTypeLabel = () => {
    if (!comment.userType) return t('comments.orgEmployee');
    const config = USER_TYPE_CONFIG[comment.userType];
    return config?.label || t('comments.orgEmployee');
  };

  return (
    <View style={[styles.commentCard, { backgroundColor: themeColors.cardBackground }]}>
      {/* Header row - Figma: Avatar → UserType+Date → Menu (RTL) */}
      <View style={styles.commentHeaderRow}>
        {/* Menu icon on LEFT (optional) - matches Figma node 2462:154730 */}
        {comment.showMenu && (
          <View style={styles.commentMenuWrapper}>
            <TouchableOpacity
              style={styles.commentMenuButton}
              onPress={() => setMenuVisible(!menuVisible)}
            >
              <EllipsisVerticalIcon size={16} color={themeColors.iconDefault} />
            </TouchableOpacity>

            {/* Dropdown Menu */}
            {menuVisible && (
              <View style={[styles.commentMenuDropdown, { backgroundColor: themeColors.dropdownBackground, borderColor: themeColors.border }]}>
                <TouchableOpacity style={styles.commentMenuItem} onPress={handleEdit}>
                  <Text style={[styles.commentMenuItemText, { color: themeColors.textPrimary }]}>{t('common.edit')}</Text>
                  <EditIcon size={16} color={themeColors.iconDefault} />
                </TouchableOpacity>
                <View style={[styles.commentMenuSeparator, { backgroundColor: themeColors.menuSeparator }]} />
                <TouchableOpacity style={styles.commentMenuItem} onPress={handleDelete}>
                  <Text style={styles.commentMenuItemTextDelete}>
                    {t('common.delete')}
                  </Text>
                  <TrashIcon size={16} color={tokens.colors.raw.red["500"]} />
                </TouchableOpacity>
              </View>
            )}
          </View>
        )}

        {/* User Type and Date - matches Figma node 2462:154687 */}
        <View style={styles.commentInfoContainer}>
          {/* User type label (not badge) - matches Figma: "فريق الاستراتيجية" or "موظف الجهة" */}
          <View style={styles.commentUserTypeRow}>
            {/* Edited tag - show inline with user type */}
            {comment.isEdited && (
              <TouchableOpacity
                style={[styles.editedTag, { backgroundColor: themeColors.editedTagBg }]}
                onPress={() => setShowOriginalPopup(true)}
              >
                <Text style={[styles.editedTagText, { color: themeColors.editedTagText }]}>{t('comments.edited')}</Text>
              </TouchableOpacity>
            )}
            <Text style={[styles.commentUserTypeText, { color: themeColors.textPrimary }]}>{getUserTypeLabel()}</Text>
          </View>
          {/* Date - matches Figma node 2462:154685 */}
          <Text style={[styles.commentDate, { color: themeColors.textMuted }]}>{comment.date}</Text>
        </View>

        {/* Avatar on RIGHT - matches Figma node 2462:154681 */}
        <View style={[styles.commentAvatar, { backgroundColor: comment.avatarColor }]}>
          <Text style={styles.commentAvatarText}>{comment.initials}</Text>
        </View>
      </View>

      {/* Separator line - matches Figma node 2462:154682 */}
      <View style={[styles.commentSeparator, { backgroundColor: themeColors.border }]} />

      {/* Comment text - matches Figma node 2462:154199 */}
      <View style={styles.commentTextContainer}>
        <Text style={[styles.commentText, { color: themeColors.textPrimary }]}>{comment.text}</Text>
      </View>

      {/* Original Text Popup Modal */}
      <Modal
        visible={showOriginalPopup}
        transparent
        animationType="fade"
        onRequestClose={() => setShowOriginalPopup(false)}
      >
        <Pressable
          style={styles.originalTextModalOverlay}
          onPress={() => setShowOriginalPopup(false)}
        >
          <View style={[styles.originalTextModalContent, { backgroundColor: themeColors.dropdownBackground, borderColor: themeColors.border }]}>
            <View style={[styles.originalTextModalHeader, { borderBottomColor: themeColors.border }]}>
              <TouchableOpacity onPress={() => setShowOriginalPopup(false)}>
                <XIcon size={18} color={themeColors.iconDefault} />
              </TouchableOpacity>
              <Text style={[styles.originalTextModalTitle, { color: themeColors.textPrimary }]}>{t('comments.originalComment')}</Text>
            </View>
            <View style={styles.originalTextModalBody}>
              <Text style={[styles.originalTextModalText, { color: themeColors.textPrimary }]}>
                {comment.originalText || t('comments.noOriginalText')}
              </Text>
            </View>
          </View>
        </Pressable>
      </Modal>
    </View>
  );
};

const getStyles = (isRTL: boolean) => StyleSheet.create({
  // Comment Card - matches Figma node 2462:154195
  commentCard: {
    backgroundColor: tokens.colors.raw.slate["100"], // general/muted
    borderRadius: tokens.radii.absolute["radius-4"],
    paddingHorizontal: tokens.spacing.absolute["2"],
    paddingVertical: tokens.spacing.absolute["1"],
  },
  // Header row - matches Figma node 2462:154679 (ArTable Cell)
  commentHeaderRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'flex-end',
    gap: tokens.spacing.absolute["2"], // absolute/2
    paddingVertical: tokens.spacing.absolute["0_5"], // absolute/0.5
  },
  // Menu button wrapper
  commentMenuWrapper: {
    position: 'relative',
    zIndex: 100,
  },
  commentMenuButton: {
    width: 16,
    height: 16,
    justifyContent: 'center',
    alignItems: 'center',
  },
  // Info container (User type + Date) - matches Figma node 2462:154687
  commentInfoContainer: {
    flex: 1,
    flexDirection: 'column',
    alignItems: 'flex-end',
    justifyContent: 'center',
  },
  // User type row (for user type text + edited tag)
  commentUserTypeRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: tokens.spacing.absolute["1_5"],
  },
  // User type text - matches Figma node 2462:154680
  commentUserTypeText: {
    fontSize: tokens.typography.paragraph["paragraph mini font size"], // paragraph/paragraph mini font size
    fontFamily: FONTS.regular,
    color: tokens.colors.semantic.general.foreground, // general/foreground
    textAlign: getTextAlign(isRTL),
    lineHeight: 14, // ~113%
  },
  // Avatar - matches Figma node 2462:154681
  commentAvatar: {
    width: 24,
    height: 24,
    borderRadius: tokens.radii.absolute["radius-12"],
    justifyContent: 'center',
    alignItems: 'center',
  },
  commentAvatarText: {
    fontSize: 10,
    fontFamily: FONTS.regular,
    color: '#FCFCFC', // dbbg-color
    textAlign: 'center',
    lineHeight: 13,
  },
  // Date - matches Figma node 2462:154685
  commentDate: {
    fontSize: 10,
    fontFamily: FONTS.regular,
    color: tokens.colors.raw.slate["500"], // general/muted-foreground
    textAlign: getTextAlign(isRTL),
    lineHeight: 20, // height from Figma
  },
  // Separator line - matches Figma node 2462:154682
  commentSeparator: {
    height: 1,
    backgroundColor: tokens.colors.raw.slate["200"], // general/border
  },
  // Comment text container - matches Figma node 2462:154196
  commentTextContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
  },
  // Comment text - matches Figma node 2462:154199
  commentText: {
    flex: 1,
    fontSize: tokens.typography.paragraph["paragraph mini font size"], // paragraph/paragraph mini font size
    fontFamily: FONTS.regular,
    color: tokens.colors.semantic.general.foreground, // general/foreground
    textAlign: getTextAlign(isRTL),
    lineHeight: 20, // height 20px
  },
  // Comment Menu dropdown
  commentMenuDropdown: {
    position: 'absolute',
    top: 20,
    start: -8,
    backgroundColor: tokens.colors.raw.white,
    borderRadius: tokens.radii.absolute["radius-12"],
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["200"],
    boxShadow: '0px 8px 20px rgba(0, 0, 0, 0.2)',
    elevation: 16,
    minWidth: 160,
    zIndex: 1000,
    paddingVertical: tokens.spacing.absolute["3"],
    paddingHorizontal: tokens.spacing.absolute["1"],
  },
  commentMenuItem: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: tokens.spacing.absolute["2_5"],
    paddingHorizontal: tokens.spacing.absolute["4"],
    paddingVertical: tokens.spacing.absolute["3"],
  },
  commentMenuItemText: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.semantic.general.foreground,
  },
  commentMenuItemTextDelete: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.raw.red["500"],
  },
  commentMenuSeparator: {
    height: 1,
    backgroundColor: tokens.colors.raw.slate["200"],
    marginHorizontal: tokens.spacing.absolute["2"],
  },
  // Edited Tag styles
  editedTag: {
    backgroundColor: tokens.colors.raw.amber["100"],
    borderRadius: tokens.radii.absolute["radius-4"],
    paddingHorizontal: tokens.spacing.absolute["1_5"],
    paddingVertical: tokens.spacing.absolute["0_5"],
  },
  editedTagText: {
    fontSize: 10,
    fontFamily: FONTS.regular,
    color: '#92400E',
  },
  // Original Text Modal styles
  originalTextModalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  originalTextModalContent: {
    backgroundColor: tokens.colors.raw.white,
    borderRadius: tokens.radii.absolute["radius-12"],
    width: '80%',
    maxWidth: 400,
    boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.25)',
    elevation: 10,
  },
  originalTextModalHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: tokens.spacing.absolute["4"],
    paddingVertical: tokens.spacing.absolute["3"],
    borderBottomWidth: tokens.spacing.absolute["px"],
    borderBottomColor: tokens.colors.raw.slate["200"],
  },
  originalTextModalTitle: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.medium,
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
  },
  originalTextModalBody: {
    paddingHorizontal: tokens.spacing.absolute["4"],
    paddingVertical: tokens.spacing.absolute["4"],
  },
  originalTextModalText: {
    fontSize: tokens.typography.paragraph["paragraph extra-small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
    lineHeight: 22,
  },
});

export default CommentCard;

const styles = getStyles(true);
